import { useCallback, useEffect, useMemo, useState } from "react";
import { SETUP_ROADMAP_PHASES } from "@/config/setup-roadmap.config.js";
import {
  SETUP_STEP_CHECKS,
  getStepCheck,
  hasReferenceRows,
  isBiaConfigPresent,
  isMatrixConfigured,
  stepCheckKey,
} from "@/config/setup-roadmap-completion.js";
import { useReferenceStore } from "@/store/reference.store.js";
import { sdk } from "@/lib/api/client.js";

function listHasRows(payload) {
  if (!payload) return false;
  if (Array.isArray(payload)) return payload.length > 0;
  if (Array.isArray(payload.data)) return payload.data.length > 0;
  if (Array.isArray(payload.items)) return payload.items.length > 0;
  const total = payload.pagination?.total ?? payload.pagination?.totalItems ?? payload.meta?.total;
  return Number(total) > 0;
}

/**
 * Data-driven setup roadmap progress for all phases.
 * Completes steps when tenant data exists — no manual ticks.
 */
export function useSetupRoadmapProgress({ enabled = true } = {}) {
  const entities = useReferenceStore((s) => s.entities);
  const seedData = useReferenceStore((s) => s.seedData);
  const [probeFlags, setProbeFlags] = useState({});
  const [probesReady, setProbesReady] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const run = async () => {
      const next = {};

      try {
        const { matrixConfigurationIntegration } = await import("@/lib/api/client.js");
        await matrixConfigurationIntegration.fetchMatrixConfiguration();
        const { useMatrixConfigurationStore } = await import("@/store/matrix-configuration.store.js");
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        next["scales.riskMatrix"] = isMatrixConfigured(mc, "riskMatrix");
        next["scales.impactProbability"] = isMatrixConfigured(mc, "riskMatrix");
        next["scales.cia"] = isMatrixConfigured(mc, "ciaMatrix");
      } catch {
        next["scales.riskMatrix"] = false;
        next["scales.impactProbability"] = false;
        next["scales.cia"] = false;
      }

      try {
        const { biaImpactConfigurationIntegration } = await import("@/lib/api/client.js");
        await biaImpactConfigurationIntegration.fetchConfiguration();
        const { default: useBiaImpactConfigurationStore } = await import(
          "@/store/bia-impact-configuration.store.js"
        );
        const cfg = useBiaImpactConfigurationStore.getState().configuration;
        next["scales.biaScale"] = isBiaConfigPresent(cfg);
      } catch {
        next["scales.biaScale"] = false;
      }

      try {
        const dutyRes = await sdk.dutyDefinition.getAll({ page: 1, pageSize: 1 });
        next["organization.duties"] = listHasRows(dutyRes);
      } catch {
        next["organization.duties"] = false;
      }

      const suggestEntries = Object.entries(SETUP_STEP_CHECKS).filter(
        ([, check]) => check.type === "suggest"
      );

      await Promise.all(
        suggestEntries.map(async ([key, check]) => {
          try {
            const res = await sdk.referenceData.suggest({
              entity: check.entity,
              q: "",
              limit: 1,
            });
            const rows = res?.data ?? res?.items ?? res;
            next[key] = listHasRows(rows);
          } catch {
            next[key] = false;
          }
        })
      );

      if (!cancelled) {
        setProbeFlags(next);
        setProbesReady(true);
      }
    };

    setProbesReady(false);
    run();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const isStepDone = useCallback(
    (phaseId, stepId) => {
      const check = getStepCheck(phaseId, stepId);
      if (!check || check.type === "exclude") return false;

      if (check.type === "reference") {
        return hasReferenceRows(entities, seedData, check.entity);
      }
      if (check.type === "referenceAny") {
        return check.entities.some((entity) => hasReferenceRows(entities, seedData, entity));
      }

      return Boolean(probeFlags[stepCheckKey(phaseId, stepId)]);
    },
    [entities, seedData, probeFlags]
  );

  const trackableSteps = useMemo(() => {
    const list = [];
    for (const phase of SETUP_ROADMAP_PHASES) {
      for (const step of phase.steps) {
        const check = getStepCheck(phase.id, step.id);
        if (check && check.type !== "exclude") {
          list.push({ phaseId: phase.id, stepId: step.id });
        }
      }
    }
    return list;
  }, []);

  const totalSteps = trackableSteps.length;

  const doneSteps = useMemo(
    () => trackableSteps.filter(({ phaseId, stepId }) => isStepDone(phaseId, stepId)).length,
    [trackableSteps, isStepDone]
  );

  const overallPercent = totalSteps === 0 ? 0 : Math.round((doneSteps / totalSteps) * 100);

  const getPhaseProgress = useCallback(
    (phase) => {
      const steps = phase.steps.filter((step) => {
        const check = getStepCheck(phase.id, step.id);
        return check && check.type !== "exclude";
      });
      const total = steps.length;
      if (total === 0) {
        return { done: 0, total: 0, percent: 0, complete: false, tracked: false };
      }
      const done = steps.filter((step) => isStepDone(phase.id, step.id)).length;
      return {
        done,
        total,
        percent: Math.round((done / total) * 100),
        complete: done === total,
        tracked: true,
      };
    },
    [isStepDone]
  );

  const resumeTarget = useMemo(() => {
    // Resume at the first incomplete phase in roadmap order (setup not finished).
    for (const phase of SETUP_ROADMAP_PHASES) {
      const progress = getPhaseProgress(phase);
      if (!progress.tracked || progress.complete) continue;
      const incomplete = phase.steps.find(
        (step) =>
          getStepCheck(phase.id, step.id)?.type !== "exclude" &&
          !isStepDone(phase.id, step.id)
      );
      return {
        phaseId: phase.id,
        stepId: incomplete?.id || phase.steps[0]?.id,
      };
    }

    const fallback =
      [...SETUP_ROADMAP_PHASES].reverse().find((phase) => getPhaseProgress(phase).tracked) ||
      SETUP_ROADMAP_PHASES[0];
    return {
      phaseId: fallback.id,
      stepId: fallback.steps[0]?.id,
    };
  }, [getPhaseProgress, isStepDone]);

  return {
    isStepDone,
    isStepTracked: (phaseId, stepId) => {
      const check = getStepCheck(phaseId, stepId);
      return Boolean(check && check.type !== "exclude");
    },
    totalSteps,
    doneSteps,
    overallPercent,
    getPhaseProgress,
    resumeTarget,
    probesReady,
  };
}

export { stepCheckKey };
